import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "../../utils/toast";
import { BOOKING_DRAFT_STORAGE_KEY } from "../../utils/bookingStorageKeys";
import { sanitizeCheckoutBookingPayload } from "../../utils/checkoutBookingResolve";
import { validateOutstationRoundTripReturnWindow } from "../../utils/outstationReturnWindow";
import { getPickupMinLeadError } from "./DateSelectModal";

const RIDE_OUTSTATION = 3;

/** Ensure guests/suitcase exist on booking root and first ride (checkout UI + API). */
function normalizeBookingDataForCheckout(data) {
  if (!data || typeof data !== "object") return data;
  const first = data.rides?.[0] || data.trips?.[0] || {};
  const pax = Number(data.no_of_passengers ?? first.no_of_passengers) || 1;
  const rawSuit = data.suitcase ?? first.suitcase;
  const suit =
    rawSuit === undefined || rawSuit === null
      ? 0
      : Math.max(0, Math.floor(Number(rawSuit)) || 0);
  const rides = Array.isArray(data.rides)
    ? data.rides.map((r, i) =>
        i === 0 ? { ...r, no_of_passengers: pax, suitcase: suit } : r,
      )
    : data.rides;
  const firstLeg = Array.isArray(rides) ? rides[0] : first;
  const scheduleFromRide =
    firstLeg?.pickup_date && firstLeg?.pickup_time
      ? `${firstLeg.pickup_date} ${firstLeg.pickup_time}`.trim()
      : null;
  const bt = Number(data.booking_type);
  const returnScheduleFromRide =
    bt === 1
      ? null
      : firstLeg?.return_date && firstLeg?.return_time
        ? `${firstLeg.return_date} ${firstLeg.return_time}`.trim()
        : null;
  const normalized = {
    ...data,
    no_of_passengers: pax,
    suitcase: suit,
    rides,
    /** Keep DateSelectModal label (e.g. "Apr 8, 2026, 01:11 PM"); only fall back to ride leg if missing. */
    schedule: data.schedule ?? scheduleFromRide,
    return_schedule:
      bt === 1 ? null : (data.return_schedule ?? returnScheduleFromRide),
  };
  return sanitizeCheckoutBookingPayload(normalized);
}

export default function PolicyModal({ bookingData }) {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPolicies = async () => {
      if (!bookingData?.ride_type) return;

      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APP_DOMAIN}/api/customer/ride-settings/${bookingData.ride_type}`,
        );
        if (response.data.status) {
          setPolicies(response.data.policies || []);
        }
      } catch (error) {
        console.error("Error fetching policies:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicies();
  }, [bookingData?.ride_type]);

  const handleProceed = (e) => {
    e.preventDefault();
    if (!bookingData?.fare_details) {
      toast.error("Please tap Check Fare first so prices are up to date.");
      return;
    }
    const modalElement = document.getElementById("updatePolicy-Modal");
    if (window.bootstrap && window.bootstrap.Modal) {
      const modalInstance =
        window.bootstrap.Modal.getOrCreateInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    } else if (modalElement) {
      modalElement.classList.remove("show");
      modalElement.style.display = "none";
      modalElement.setAttribute("aria-hidden", "true");
      document.body.classList.remove("modal-open");
      const backdrop = document.querySelector(".modal-backdrop");
      if (backdrop) backdrop.remove();
    }
    // Do not merge orbi_booking_draft here — stale session draft overwrites fresh Check Fare data.
    const normalized = normalizeBookingDataForCheckout(bookingData);
    const pickupLeadErr = getPickupMinLeadError(normalized?.schedule);
    if (pickupLeadErr) {
      toast.error(pickupLeadErr);
      return;
    }
    if (
      Number(normalized?.ride_type) === RIDE_OUTSTATION &&
      Number(normalized?.booking_type) === 2
    ) {
      const validation = validateOutstationRoundTripReturnWindow(
        normalized.schedule,
        normalized.return_schedule,
      );
      if (!validation.ok) {
        toast.error(validation.message);
        return;
      }
    }
    const rev = Date.now();
    try {
      sessionStorage.setItem(
        BOOKING_DRAFT_STORAGE_KEY,
        JSON.stringify({ ts: rev, data: normalized }),
      );
    } catch (_) {
      /* ignore quota / private mode */
    }
    navigate("/checkout", {
      replace: true,
      state: {
        bookingData: normalized,
        _checkoutRevision: rev,
      },
    });
  };

  return (
    <div
      className="modal moreInfo_modal fare_modal fade"
      id="updatePolicy-Modal"
      tabIndex="-1"
      aria-labelledby="policyModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog moreInfo_modal-dialog">
        <div className="modal-content moreInfo_modal-content">
          <div className="modal-header moreInfo_modal-header">
            <h1 className="modal-title fs-5" id="policyModalLabel">
              Updated Policy
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body moreInfo_modal-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-warning" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : policies.length > 0 ? (
              <div className="policy_list">
                {policies.map((policy, idx) => (
                  <div
                    className="policy_col"
                    key={policy.id ?? `${policy.title || "policy"}-${idx}`}
                  >
                    <img
                      src={policy.image_url}
                      alt={policy.title || "Policy"}
                      onError={(e) => {
                        e.target.src = "/assets/img/all-images/Clock-icon-yellow.png";
                      }}
                    />
                    <p>
                      {policy.title || "Policy Update"}
                      {policy.description && (
                        <>
                          <br />
                          <small>{policy.description}</small>
                        </>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="body_text pt-3">
              <p className="mb-3">
                We reserve the right to cancel the ride after 30 minutes of
                waiting. No cancellation fee if we're late, but waiting charges
                still apply.
              </p>
              <p>
                By booking this ride, I consent to Orbicabs terms of service.{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {" "}
                  Learn more
                </a>
              </p>
            </div>
          </div>
          <div className="modal-footer moreInfo_modal-footer">
            <button
              type="button"
              onClick={handleProceed}
              className="btn"
              disabled={loading}
            >
              Accept & Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

